import { env } from './env.ts';
import { KeyValueStore } from './storage.ts';
import type { DaoUser } from '@/types/dao.ts';

/**
 * 소셜 로그인 팝업 ↔ 부모창 통신 규약.
 *
 * 메시지 타입 이름(NAVER_SUCCESS 등)은 백엔드의 카카오 팝업 엔드포인트가
 * 이미 이 형식으로 postMessage 하고 있어 그대로 유지한다. 바꾸려면 백엔드도
 * 같이 바꿔야 한다.
 */
export type OauthSuccessType = 'NAVER_SUCCESS' | 'KAKAO_SUCCESS' | 'GOOGLE_SUCCESS';
export type OauthFailType = 'NAVER_FAIL' | 'KAKAO_FAIL' | 'GOOGLE_FAIL';

export type OauthMessage =
  { type: OauthSuccessType; payload: DaoUser } | { type: OauthFailType; payload: string };

const SUCCESS_TYPES: readonly string[] = ['NAVER_SUCCESS', 'KAKAO_SUCCESS', 'GOOGLE_SUCCESS'];
const FAIL_TYPES: readonly string[] = ['NAVER_FAIL', 'KAKAO_FAIL', 'GOOGLE_FAIL'];

export function isOauthSuccess(
  msg: OauthMessage
): msg is { type: OauthSuccessType; payload: DaoUser } {
  return SUCCESS_TYPES.includes(msg.type);
}

/** postMessage 로 들어온 값이 우리 규약의 메시지인지 */
export function isOauthMessage(data: unknown): data is OauthMessage {
  if (!data || typeof data !== 'object') return false;

  const type = (data as { type?: unknown }).type;
  return typeof type === 'string' && (SUCCESS_TYPES.includes(type) || FAIL_TYPES.includes(type));
}

/**
 * 메시지를 신뢰할 수 있는 출처에서 온 것인지 확인한다.
 *
 * 네이버는 우리 프론트 콜백 라우트(같은 origin)에서, 카카오는 백엔드 팝업
 * 엔드포인트(API origin)에서 보낸다. 구 프로젝트는 origin 검사가 주석 처리돼
 * 있어 아무 창이나 로그인 성공 메시지를 밀어넣을 수 있었다.
 */
export function isTrustedOauthOrigin(origin: string): boolean {
  if (origin === window.location.origin) return true;

  try {
    return origin === new URL(env.apiBase).origin;
  } catch {
    return false;
  }
}

/** 팝업(또는 콜백 페이지)에서 부모창으로 결과를 보낸다. */
export function postOauthResult(message: OauthMessage): void {
  // 부모창이 어느 origin 인지 팝업 쪽에서 알 수 없으므로 '*' 로 보내되,
  // payload 에 민감정보(토큰)를 담지 않는다. 수신측에서 origin 을 검증한다.
  window.opener?.postMessage(message, '*');
}

/**
 * 팝업에 "메시지 잘 받았다"고 알린다.
 *
 * postMessage 는 targetOrigin 이 어긋나면 예외 없이 조용히 버려진다. 팝업은 이
 * 응답이 오지 않으면 창을 닫지 않고 원인을 화면에 띄우도록 되어 있다.
 */
export function ackOauthMessage(event: MessageEvent): void {
  const source = event.source as Window | null;
  source?.postMessage('OAUTH_ACK', event.origin);
}

/** 화면 가운데에 로그인 팝업을 띄운다. */
export function openOauthPopup(
  url: string,
  name: string,
  width = 500,
  height = 700
): Window | null {
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

  return window.open(
    url,
    name,
    `width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)}`
  );
}

/**
 * 백엔드 오류 응답에서 사람이 읽을 메시지를 뽑는다.
 *
 * PHP 쪽 ControllerApi::setError() 는 Content-Type 없이
 * `{"ok":false,"error":"..."}` 를 그대로 내보낸다. 파싱하지 않으면 사용자에게
 * JSON 문자열이 그대로 보인다.
 */
function readErrorMessage(text: string, status: number): string {
  try {
    const parsed = JSON.parse(text) as { error?: string; message?: string };
    const message = parsed.error ?? parsed.message;
    if (message) return message;
  } catch {
    // JSON 이 아니면 아래에서 원문을 살핀다.
  }

  const fallback = `로그인에 실패했습니다. 잠시 후 다시 시도해 주세요. (${status})`;
  const trimmed = text.trim();

  /*
    ★ **HTML 이면 보여주지 않는다.** (2026-09-17)
      서버가 JSON 대신 오류 페이지(404·500·프록시 오류)를 돌려주면, 예전에는 그 원문이
      그대로 알림에 떴다 — 실제로 `<!DOCTYPE html>...` 이 사용자에게 보였다.
  */
  if (!trimmed || trimmed.startsWith('<') || /<html[\s>]/i.test(trimmed)) return fallback;

  // 평문이라도 너무 길면 알림이 화면을 덮는다.
  return trimmed.length > 200 ? fallback : trimmed;
}

/**
 * 백엔드의 OAuth 교환 엔드포인트를 호출한다.
 * 엔드포인트가 전체 URL(VITE_*_API)이라 공용 api 래퍼 대신 fetch 를 직접 쓴다.
 *
 * 구글(`google.json`)·네이버(`naver.json`) 는 성공 시 `{result, message, data}`
 * 봉투 없이 회원 객체를 그대로 돌려준다.
 */
export async function exchangeOauth(
  endpoint: string,
  body: Record<string, unknown>
): Promise<DaoUser> {
  if (!endpoint) throw new Error('OAuth 엔드포인트가 설정되지 않았습니다. .env 를 확인하세요.');

  /*
    ★ 연결 자체가 실패하면 브라우저가 영어 원문(`Failed to fetch`)을 던진다.
      그대로 알림에 뜨면 사용자는 무슨 뜻인지 모른다. 한국어 안내로 바꾼다.
  */
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('서버에 연결하지 못했습니다. 인터넷 연결을 확인하고 다시 시도해 주세요.');
  }

  if (!res.ok) {
    throw new Error(readErrorMessage(await res.text().catch(() => ''), res.status));
  }

  const user = (await res.json().catch(() => null)) as DaoUser | null;
  if (!user?.id) throw new Error('로그인 응답이 비어 있습니다.');

  return user;
}

/**
 * 로그인 뒤 돌아갈 주소로 쓸 수 있는 값만 남긴다. 아니면 홈.
 *
 * `//evil.com` 같은 프로토콜 상대 주소는 **다른 사이트로 보내는 통로**가 되므로 막는다.
 */
export function sanitizeReturnTo(raw?: string | null): string {
  if (!raw) return '/';
  return raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
}

/** 지금 주소의 `?r=` (로그인 뒤 돌아갈 곳) */
export function readReturnTo(): string {
  return sanitizeReturnTo(new URLSearchParams(window.location.search).get('r'));
}

/**
 * 로그인 뒤 돌아갈 곳을 **잠깐 맡겨 둔다.** (2026-09-17)
 *
 * 인앱 브라우저에서는 페이지 전체가 카카오로 갔다가 돌아오므로 `?r=` 이 사라진다.
 * 돌아온 콜백 화면이 여기서 꺼내 쓴다.
 *
 * ★ **localStorage 를 쓴다.** 팝업이 원래 창을 못 찾아 스스로 사이트로 돌아가는
 *   경우(비상구)는 **다른 창**이라 sessionStorage 로는 넘어가지 않는다.
 * ★ 10분만 둔다. 로그인 한 번이 그보다 오래 걸릴 일은 없다.
 */
const returnStore = new KeyValueStore<'oauth-return'>({ prefix: 'app:', driver: 'local' });
const OAUTH_RETURN_TTL_MS = 10 * 60 * 1000;

export function saveOauthReturn(path: string): void {
  returnStore.set<string>('oauth-return', sanitizeReturnTo(path), OAUTH_RETURN_TTL_MS);
}

/** 맡겨 둔 돌아갈 곳을 꺼낸다(꺼내면 지운다). 없으면 홈. */
export function takeOauthReturn(): string {
  const saved = returnStore.get<string>('oauth-return');
  returnStore.remove('oauth-return');
  return sanitizeReturnTo(saved);
}

/**
 * 1회용 로그인 티켓을 로그인 결과(회원 + 토큰)로 바꾼다.
 *
 * 인앱 브라우저의 카카오 로그인에서 백엔드가 `/oauth/kakao.callback?ticket=...` 으로
 * 돌려보낸다. 로그인 토큰은 주소에 싣지 않고 이 티켓만 싣는다 — 티켓은 한 번 쓰면
 * 끝이고 5분이면 만료된다. 응답 모양은 구글·네이버 로그인과 같다.
 */
export function redeemLoginTicket(ticket: string): Promise<DaoUser> {
  return exchangeOauth(`${env.apiBase.replace(/\/+$/, '')}/api/oauth/ticket.json`, { ticket });
}
